import { memo, useCallback, useMemo, useState } from "react";
import { useTranslate } from "helpers";
import { updateFleetVehicleMileageAPI } from "services/api/fleet/vehicles/api";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { NumberField } from "ui/components/TextField/NumberField";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { VehicleMileageModalProps, VehicleMileageFormValues } from "./types";
import { FLEET_VEHICLE_MILEAGE_FORM_SCHEMA } from "./validation";

const MilageEditModal: React.FC<VehicleMileageModalProps> = (props) => {
    const { show, onClose, onSuccess, vehicleId, currentMileage } = props;

    const { t } = useTranslate();
    const [isLoading, setIsLoading] = useState(false);
    const [formValues, setFormValues] = useState<VehicleMileageFormValues>({
        kmTraveled: currentMileage,
    });
    const [errors, setErrors] = useState<Errors>({});

    const schema = useMemo(() => FLEET_VEHICLE_MILEAGE_FORM_SCHEMA(currentMileage), [currentMileage]);

    const handleChange = useCallback((value: number | null) => {
        setFormValues({ kmTraveled: value });
        setErrors({});
    }, []);

    const handleSubmit = async () => {
        const isValid = validateYup(formValues, schema, setErrors);
        if (!isValid) {
            return;
        }

        setIsLoading(true);
        try {
            await updateFleetVehicleMileageAPI(vehicleId, formValues.kmTraveled!, t);
            onSuccess(formValues.kmTraveled!);
            handleClose();
        } catch (error) {
            console.error("Mileage update failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = useCallback(() => {
        setFormValues({ kmTraveled: currentMileage });
        setErrors({});
        onClose();
    }, [onClose, currentMileage]);

    return (
        <Modal show={show} onHide={handleClose} size="small" position="centered">
            <ModalHeader
                icon="/media/images/add-icon.svg"
                title={t("admin.fleet.vehicle.mileage.modal.title")}
                onHide={handleClose}
            />
            <ModalBody>
                <div className="space-y-4 p-4">
                    <p className="text-sm text-gray-600">
                        {t("admin.fleet.vehicle.mileage.modal.description")}
                    </p>
                    <NumberField
                        label={t("admin.fleet.vehicle.detail.info.kmtraveled")}
                        name="kmTraveled"
                        value={formValues.kmTraveled}
                        onChange={handleChange}
                        error={errors.kmTraveled}
                        required
                        decimals={0}
                        groupAppend="km"
                        size="full"
                    />
                    {errors.kmTraveled && (
                        <p className="text-sm text-red-500 mt-1">{errors.kmTraveled}</p>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="px-4 py-3 flex justify-end gap-3">
                      <Button variant="outlined" onClick={handleClose}>
                        {t("admin.cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isLoading}
                    >
                        {t("admin.save")}
                    </Button>
                  
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default memo(MilageEditModal);